import { useNavigation } from "@react-navigation/native";
import React, { useCallback, useContext } from "react";
import { graphql, useMutation } from "react-relay";
import { UserLoggedInNavigatorNavigationProp } from "../../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { EmptyPositionT } from "../../../EmptyPositions/EmptyPosType";

import { WidgetSizeT } from "../../../localTypes/WidgetSizeT";
import { taskSingleWidgetSizes } from "../../../WidgetAdd/utils/widgetSizes/taskSingleWidgetSizes";
import { WAWidgetSizeToTaskSingleSize } from "../../../WidgetAdd/utils/WAWidgetSizeToTaskSingleSize";
import { WATaskSingleContext } from "./WATaskSingleContext";
import { WidgetTaskSinglePreview } from "../WidgetTaskSinglePreview";
import { WATaskSingleSizePreviewMutation } from "./__generated__/WATaskSingleSizePreviewMutation.graphql";
import { WASizePreview } from "../../../WidgetAdd/WASizePreview/WASizePreview";

type Props = {
  taskId: string | null;
  unitSize: number;
  maxSize: WidgetSizeT;
  storecastId: string;
  position: EmptyPositionT;
};

export function WATaskSingleSizePreview(props: Props) {
  const context = useContext(WATaskSingleContext);
  const navigation =
    useNavigation<UserLoggedInNavigatorNavigationProp<"WidgetAddTaskSingle">>();
  const [createWidgetTaskSingle, loading] =
    useMutation<WATaskSingleSizePreviewMutation>(graphql`
      mutation WATaskSingleSizePreviewMutation(
        $storecastId: ID!
        $input: CreateWidgetTaskSingleInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetTaskSingle(widget: $input) {
            id
          }
        }
      }
    `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      if (context.taskId === null) {
        return;
      }
      createWidgetTaskSingle({
        variables: {
          storecastId: props.storecastId,
          input: {
            positionX: props.position.x + props.position.c * 2,
            positionY: props.position.y,
            size: WAWidgetSizeToTaskSingleSize(size),
            taskId: context.taskId,
            grouping: "SECTION",
            sectionIds: context.sectionIds,
            teamIds: [],
          },
        },
        onCompleted: () => {
          navigation.pop(2);
        },
      });
    },
    [
      context.sectionIds,
      context.taskId,
      createWidgetTaskSingle,
      navigation,
      props.position.c,
      props.position.x,
      props.position.y,
      props.storecastId,
    ]
  );

  const renderWidgetPreview = useCallback(
    (size: WidgetSizeT) => {
      if (context.taskId === null) {
        return null;
      }
      return (
        <WidgetTaskSinglePreview
          sectionIds={context.sectionIds}
          storecastId={context.storecastId}
          taskId={context.taskId}
          clientForceSize={size.w === 2 ? "TWO_X_ONE" : "ONE_X_ONE"}
        />
      );
    },
    [context.sectionIds, context.storecastId, context.taskId]
  );

  return (
    <WASizePreview
      disabled={context.taskId === null}
      onCreate={onCreate}
      loading={loading}
      unitSize={props.unitSize}
      maxSize={props.maxSize}
      widgetSizes={taskSingleWidgetSizes}
    >
      {renderWidgetPreview}
    </WASizePreview>
  );
}
